import { useAuthStore } from '../store/authStore';
import {
  finalizeMessageArchivePeriodsForCircle,
  writeCurrentMessageArchivePeriod
} from '../messageArchive/writer';
import { createCurrentCircleArchiveDestination } from '../messageArchive/destination';

const EVENT_WRITE_DELAY_MS = 30_000;
const MIN_WRITE_INTERVAL_MS = 5 * 60 * 1000;
const PERIODIC_WRITE_INTERVAL_MS = 15 * 60 * 1000;

let initialized = false;
let scheduledTimer: number | null = null;
let inFlight = false;
let lastRunAt = 0;

function getWritableArchiveIdentityIds(): string[] {
  const state = useAuthStore.getState();
  if (!state.masterKey) return [];
  const deviceByIdentity = new Map(Object.values(state.devices).map((device) => [device.identityId, device.deviceId]));
  return Object.values(state.identities)
    .filter((identity) => deviceByIdentity.has(identity.identityId))
    .map((identity) => identity.identityId);
}

async function isCurrentDeviceWriter(identityId: string): Promise<boolean> {
  const state = useAuthStore.getState();
  const identity = state.identities[identityId];
  const device = Object.values(state.devices).find((item) => item.identityId === identityId);
  if (!identity || !device || !state.masterKey) return false;

  const destination = createCurrentCircleArchiveDestination({
    sourceCircleIdentityId: identity.identityId,
    serverUrl: identity.serverUrl
  });
  const job = await destination.getJob();
  if (!job) return false;
  return job.status === 'active' && job.writerDeviceId === device.deviceId;
}

async function runArchiveWriterOnce(reason: string): Promise<void> {
  if (inFlight) return;
  const now = Date.now();
  if (now - lastRunAt < MIN_WRITE_INTERVAL_MS && reason !== 'periodic') return;

  inFlight = true;
  lastRunAt = now;
  try {
    const identities = getWritableArchiveIdentityIds();
    for (const identityId of identities) {
      const identity = useAuthStore.getState().identities[identityId];
      if (!identity) continue;
      try {
        if (!await isCurrentDeviceWriter(identityId)) continue;
        await finalizeMessageArchivePeriodsForCircle({
          sourceCircleIdentityId: identity.identityId,
          serverUrl: identity.serverUrl
        });
        await writeCurrentMessageArchivePeriod({
          sourceCircleIdentityId: identity.identityId,
          serverUrl: identity.serverUrl
        });
      } catch (error) {
        console.debug('[MessageArchive] writer run skipped', {
          reason,
          identityId,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
  } finally {
    inFlight = false;
  }
}

function scheduleArchiveWriter(reason: string, delayMs = EVENT_WRITE_DELAY_MS): void {
  if (scheduledTimer !== null) {
    window.clearTimeout(scheduledTimer);
  }
  scheduledTimer = window.setTimeout(() => {
    scheduledTimer = null;
    void runArchiveWriterOnce(reason);
  }, delayMs);
}

export function initializeMessageArchiveWriter(): void {
  if (initialized) return;
  initialized = true;

  const onLocalMessagesChanged = () => scheduleArchiveWriter('local-message-change');
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      scheduleArchiveWriter('foreground', 5_000);
    }
  };

  window.addEventListener('messages:updated', onLocalMessagesChanged);
  window.addEventListener('group-chats:updated', onLocalMessagesChanged);
  document.addEventListener('visibilitychange', onVisibilityChange);

  window.setInterval(() => {
    void runArchiveWriterOnce('periodic');
  }, PERIODIC_WRITE_INTERVAL_MS);

  scheduleArchiveWriter('startup', 10_000);
}
